import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  DollarSign,
  ShoppingCart,
  Users,
  Package,
  TrendingUp,
  ArrowUpRight,
  ArrowDownRight,
  Loader2,
  RefreshCw,
  Calendar,
} from "lucide-react";
import {
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { formatCurrency, formatNumber, formatPercentage } from "../lib/utils";
import { useProductStatistics } from "../hooks/useProductStatistics";
import { useOrderStatistics } from "../hooks/useOrderStatistics";
import { useCustomerStatistics } from "../hooks/useCustomerStatistics";
import { useRevenueTrend } from "../hooks/useRevenueTrend";
import { useTopProducts } from "../hooks/useTopProducts";
import { useOrderStatus } from "../hooks/useOrderStatus";
import {
  NotificationService,
  NotificationData,
} from "../services/notificationService";
import { toast } from "sonner";
import { useState, useEffect } from "react";

const MetricCard = ({
  title,
  value,
  change,
  icon: Icon,
  format = "number",
  loading = false,
}: any) => {
  const isPositive = change > 0;
  const formattedValue =
    format === "currency" ? formatCurrency(value) : formatNumber(value);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      whileHover={{ y: -2 }}
      className="metric-card"
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          {loading ? (
            <div className="flex items-center gap-2 mt-1">
              <Loader2 className="w-4 h-4 animate-spin text-jamaica-green" />
              <span className="text-sm text-gray-500">Loading...</span>
            </div>
          ) : (
            <p className="text-2xl font-bold text-gray-900 mt-1">
              {formattedValue}
            </p>
          )}
          <div className="flex items-center mt-2">
            {loading ? (
              <span className="text-sm text-gray-500">
                Calculating trend...
              </span>
            ) : (
              <>
                {isPositive ? (
                  <ArrowUpRight className="w-4 h-4 text-green-500" />
                ) : (
                  <ArrowDownRight className="w-4 h-4 text-red-500" />
                )}
                <span
                  className={`text-sm font-medium ml-1 ${
                    isPositive ? "text-green-600" : "text-red-600"
                  }`}
                >
                  {formatPercentage(Math.abs(change))}
                </span>
                <span className="text-sm text-gray-500 ml-1">
                  vs last month
                </span>
              </>
            )}
          </div>
        </div>
        <div className="w-12 h-12 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-lg flex items-center justify-center">
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </motion.div>
  );
};

export function Dashboard() {
  const navigate = useNavigate();
  const [lastRefreshed, setLastRefreshed] = useState<Date>(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [recentNotifications, setRecentNotifications] = useState<
    NotificationData[]
  >([]);

  const {
    statistics: productStatistics,
    percentageChanges: productPercentageChanges,
    loading: productStatisticsLoading,
    refresh: refreshProductStatistics,
  } = useProductStatistics();

  const {
    statistics: orderStatistics,
    percentageChanges: orderPercentageChanges,
    loading: orderStatisticsLoading,
    refresh: refreshOrderStatistics,
  } = useOrderStatistics();

  const {
    statistics: customerStatistics,
    percentageChanges: customerPercentageChanges,
    loading: customerStatisticsLoading,
    refresh: refreshCustomerStatistics,
  } = useCustomerStatistics();

  const {
    data: revenueData,
    loading: revenueLoading,
    error: revenueError,
    refresh: refreshRevenueTrend,
  } = useRevenueTrend();

  const {
    data: topProducts,
    loading: topProductsLoading,
    error: topProductsError,
    refresh: refreshTopProducts,
  } = useTopProducts(5);

  const {
    data: orderStatusCounts,
    loading: orderStatusLoading,
    error: orderStatusError,
    refresh: refreshOrderStatus,
  } = useOrderStatus();

  // Set up real-time notifications listener
  useEffect(() => {
    const unsubscribe = NotificationService.getNotificationsListener(
      (notifications) => {
        // Get only the 4 most recent notifications
        setRecentNotifications(notifications.slice(0, 4));
      },
      4 // Limit to 4 notifications
    );

    return () => unsubscribe();
  }, []);

  // Handle notification click with navigation
  const handleNotificationClick = (notification: NotificationData) => {
    const actionUrl = NotificationService.getActionUrl(notification);
    navigate(actionUrl);

    // Mark as read when clicked
    if (!notification.read) {
      NotificationService.markAsRead(notification.id).catch(console.error);
    }
  };

  // Convert notification to activity format for consistent UI
  const convertNotificationToActivity = (notification: NotificationData) => {
    return {
      id: notification.id,
      type: notification.type,
      title: notification.title,
      description: notification.message,
      time: NotificationService.formatTime(notification.createdAt),
    };
  };

  // Handle refresh for all dashboard data
  const handleRefresh = async () => {
    try {
      setIsRefreshing(true);
      await Promise.all([
        refreshProductStatistics(),
        refreshOrderStatistics(),
        refreshCustomerStatistics(),
        refreshRevenueTrend(),
        refreshTopProducts(),
        refreshOrderStatus(),
      ]);
      setLastRefreshed(new Date());
      toast.success("Dashboard data refreshed successfully");
    } catch (error) {
      console.error("Error refreshing dashboard data:", error);
      toast.error("Failed to refresh dashboard data. Please try again.");
    } finally {
      setIsRefreshing(false);
    }
  };

  // Update last refreshed time on component mount
  useEffect(() => {
    setLastRefreshed(new Date());
  }, []);

  // Get real data or fallback to 0
  const totalProducts = productStatistics?.totalProducts ?? 0;
  const productsChange = productPercentageChanges?.totalProducts ?? 0;

  const totalRevenue = orderStatistics?.totalRevenue ?? 0;
  const revenueChange = orderPercentageChanges?.totalRevenue ?? 0;

  const totalOrders = orderStatistics?.totalOrders ?? 0;
  const ordersChange = orderPercentageChanges?.totalOrders ?? 0;

  // Create orderStatusData from real data
  const orderStatusData = [
    { name: "Delivered", value: orderStatusCounts.delivered, color: "#00742f" },
    { name: "Shipped", value: orderStatusCounts.shipped, color: "#ffc901" },
    {
      name: "Processed",
      value: orderStatusCounts.processed,
      color: "#22c55e",
    },
    {
      name: "Processing",
      value: orderStatusCounts.processing,
      color: "#3b82f6",
    },
    { name: "Cancelled", value: orderStatusCounts.cancelled, color: "#ef4444" },
  ];

  // Format current date and time
  const formatCurrentDate = () => {
    const now = new Date();
    const options: Intl.DateTimeFormatOptions = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    return now.toLocaleDateString("en-US", options);
  };

  const formatLastRefreshed = () => {
    return lastRefreshed.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="space-y-6">
      {/* Header Section with Date and Refresh */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4"
      >
        <div className="space-y-2">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
          >
            <span className="text-sm font-semibold text-gray-900">
              {formatCurrentDate()}
            </span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="flex items-center gap-2 text-xs text-gray-600"
          >
            <span>Last updated:</span>
            <motion.span
              key={lastRefreshed.getTime()}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className="font-medium text-jamaica-green"
            >
              {formatLastRefreshed()}
            </motion.span>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
        >
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="flex items-center gap-2 transition-all duration-200 hover:border-jamaica-green hover:text-jamaica-green"
          >
            {isRefreshing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            {isRefreshing ? "Refreshing..." : "Refresh Data"}
          </Button>
        </motion.div>
      </motion.div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Total Revenue"
          value={totalRevenue}
          change={revenueChange}
          icon={DollarSign}
          format="currency"
          loading={orderStatisticsLoading}
        />
        <MetricCard
          title="Total Orders"
          value={totalOrders}
          change={ordersChange}
          icon={ShoppingCart}
          loading={orderStatisticsLoading}
        />
        <MetricCard
          title="Total Customers"
          value={customerStatistics?.totalCustomers ?? 0}
          change={customerPercentageChanges?.totalCustomers ?? 0}
          icon={Users}
          loading={customerStatisticsLoading}
        />
        <MetricCard
          title="Total Products"
          value={totalProducts}
          change={productsChange}
          icon={Package}
          loading={productStatisticsLoading}
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Chart */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Card className="admin-card">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <TrendingUp className="w-5 h-5 text-jamaica-green" />
                <span>Revenue Trend</span>
              </CardTitle>
              <CardDescription>Daily revenue for the past week</CardDescription>
            </CardHeader>
            <CardContent>
              {revenueLoading ? (
                <div className="flex items-center justify-center h-[300px]">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-6 h-6 animate-spin text-jamaica-green" />
                    <span className="text-gray-500">
                      Loading revenue data...
                    </span>
                  </div>
                </div>
              ) : revenueError ? (
                <div className="flex items-center justify-center h-[300px]">
                  <div className="text-center">
                    <p className="text-red-600 font-medium">
                      Error loading data
                    </p>
                    <p className="text-gray-500 text-sm">{revenueError}</p>
                  </div>
                </div>
              ) : revenueData.length === 0 ? (
                <div className="flex items-center justify-center h-[300px]">
                  <p className="text-gray-500">No revenue data available</p>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={revenueData}>
                    <defs>
                      <linearGradient
                        id="revenueGradient"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor="#00742f"
                          stopOpacity={0.3}
                        />
                        <stop
                          offset="95%"
                          stopColor="#00742f"
                          stopOpacity={0}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => {
                        // Parse the date string (YYYY-MM-DD) without timezone conversion
                        const [year, month, day] = value.split("-").map(Number);
                        const date = new Date(year, month - 1, day);
                        return date.toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                        });
                      }}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip
                      formatter={(value: any, name: string) => [
                        name === "revenue" ? formatCurrency(value) : value,
                        name === "revenue" ? "Revenue" : "Orders",
                      ]}
                      labelFormatter={(label) => {
                        // Parse the date string (YYYY-MM-DD) without timezone conversion
                        const [year, month, day] = label.split("-").map(Number);
                        const date = new Date(year, month - 1, day);
                        return date.toLocaleDateString("en-US", {
                          weekday: "long",
                          month: "short",
                          day: "numeric",
                        });
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="revenue"
                      stroke="#00742f"
                      strokeWidth={2}
                      fill="url(#revenueGradient)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Order Status Chart */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <Card className="admin-card">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <ShoppingCart className="w-5 h-5 text-jamaica-green" />
                <span>Order Status</span>
              </CardTitle>
              <CardDescription>
                Distribution of order statuses for current month
              </CardDescription>
            </CardHeader>
            <CardContent>
              {orderStatusLoading ? (
                <div className="flex items-center justify-center h-[300px]">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-6 h-6 animate-spin text-jamaica-green" />
                    <span className="text-gray-500">
                      Loading order status data...
                    </span>
                  </div>
                </div>
              ) : orderStatusError ? (
                <div className="flex items-center justify-center h-[300px]">
                  <div className="text-center">
                    <p className="text-red-600 font-medium">
                      Error loading order status
                    </p>
                    <p className="text-gray-500 text-sm">{orderStatusError}</p>
                  </div>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={orderStatusData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {orderStatusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value: any) => [`${value} orders`, "Count"]}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Products */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <Card className="admin-card">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Package className="w-5 h-5 text-jamaica-green" />
                <span>Top Products This Month</span>
              </CardTitle>
              <CardDescription>
                Best selling products this month
              </CardDescription>
            </CardHeader>
            <CardContent>
              {topProductsLoading ? (
                <div className="flex items-center justify-center h-[300px]">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-6 h-6 animate-spin text-jamaica-green" />
                    <span className="text-gray-500">
                      Loading top products...
                    </span>
                  </div>
                </div>
              ) : topProductsError ? (
                <div className="flex items-center justify-center h-[300px]">
                  <div className="text-center">
                    <p className="text-red-600 font-medium">
                      Error loading products
                    </p>
                    <p className="text-gray-500 text-sm">{topProductsError}</p>
                  </div>
                </div>
              ) : topProducts.length === 0 ? (
                <div className="flex items-center justify-center h-[300px]">
                  <p className="text-gray-500">
                    No sales data available for this month
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {topProducts.map((product, index) => (
                    <motion.div
                      key={product.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: 0.1 * index }}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">
                          {product.name}
                        </p>
                        <p className="text-sm text-gray-600">
                          {product.salesThisMonth || 0} sales this month
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-jamaica-green">
                          {formatCurrency(product.revenueThisMonth || 0)}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Recent Activity */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <Card className="admin-card">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <TrendingUp className="w-5 h-5 text-jamaica-green" />
                <span>Recent Activity</span>
              </CardTitle>
              <CardDescription>
                Latest updates and notifications
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recentNotifications.map((notification, index) => {
                  const activity = convertNotificationToActivity(notification);
                  return (
                    <motion.div
                      key={notification.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: 0.1 * index }}
                      className="flex items-start space-x-3 p-3 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer"
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="w-2 h-2 bg-jamaica-green rounded-full mt-2 flex-shrink-0"></div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900">
                          {activity.title}
                        </p>
                        <p className="text-sm text-gray-600">
                          {activity.description}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {activity.time}
                        </p>
                      </div>
                    </motion.div>
                  );
                })}
                {recentNotifications.length === 0 && (
                  <div className="flex items-center justify-center py-8">
                    <p className="text-gray-500 text-sm">
                      No recent notifications
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
